import { Icon, JsxStyleProps, Portal } from '@chakra-ui/react';
import {
  INewQuoteTypeReqSchema,
  IQuoteRespSchema,
  IRevenueMetricsQuoteItem,
  QuoteStatusEnum,
} from '@monetize/types/app';
import { FunctionComponent as FC, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { MdClose } from 'react-icons/md';
import { useQuoteRevenueMetrics } from '../../../api/cpqService';
import { useRevRecSettings } from '../../../api/revenueService';
import { RECURRING_REVENUE_PRODUCT_TYPES } from '../../../constants/products';
import { useACL } from '../../../services/acl/acl';
import { useFlags } from '../../../services/launchDarkly';
import {
  MBox,
  MButton,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MDropdownActionItem,
  MFlex,
  MSkeleton,
  MTab,
  MTabList,
  MTabPanel,
  MTabPanels,
  MTabs,
  MText,
} from '../../Monetize';
import { QuoteAnalyticsCard } from './QuoteAnalyticsCard';
import { QuoteAnalyticsInfoCard } from './QuoteAnalyticsInfoCard';
import { QuoteAnalyticsManualRenewalForm } from './QuoteAnalyticsManualRenewalForm';
import QuoteAnalyticsRevenueCard from './QuoteAnalyticsRevenueCard';
import { QuoteAnnualBreakdownAnalyticsCard } from './QuoteBreakdownAnalyticsCard';

interface QuoteAnalyticsV2Props {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  tabIndex?: number;
  quote: IQuoteRespSchema;
  isReadOnly?: boolean;
  setQuote: (quote: IQuoteRespSchema | null) => void;
  manualRenewalFormMethods?: UseFormReturn<INewQuoteTypeReqSchema, object>;
  iconButtonStyles?: JsxStyleProps;
  renderTrigger?: () => React.ReactElement | null;
}

interface AnalyticsItem {
  label: string;
  amount: number;
  incrementalAmount?: number;
  offerings?: {
    name: string;
    amount: number;
    incrementalAmount?: number;
    products: {
      name: string;
      amount: number;
      incrementalAmount?: number;
    }[];
  }[];
}

interface AnalyticsCards {
  arr: AnalyticsItem;
  mrr: AnalyticsItem;
}

function getAnalyticsItem(
  quote: IQuoteRespSchema,
  label: string,
  type: keyof IRevenueMetricsQuoteItem,
  incrementalType: keyof IRevenueMetricsQuoteItem,
): AnalyticsItem {
  const { revenueMetrics, quoteOfferings } = quote;
  return {
    label,
    amount: revenueMetrics[type],
    incrementalAmount: revenueMetrics[incrementalType],
    offerings: quoteOfferings.map((quoteOffering) => ({
      amount: quoteOffering.revenueMetrics[type],
      incrementalAmount: quoteOffering.revenueMetrics[incrementalType],
      name: quoteOffering.offeringName,
      products: quoteOffering.items
        .filter((quoteItem) =>
          RECURRING_REVENUE_PRODUCT_TYPES.has(quoteItem.productType),
        )
        .map((quoteItem) => ({
          name: quoteItem.productName,
          amount: quoteItem.revenueMetrics[type],
          incrementalAmount: quoteItem.revenueMetrics[incrementalType],
        })),
    })),
  };
}

export const QuoteAnalyticsV2: FC<QuoteAnalyticsV2Props> = ({
  quote,
  isReadOnly,
  iconButtonStyles = {},
  setQuote,
  manualRenewalFormMethods,
  tabIndex = 0,
  renderTrigger,
  isOpen,
  onOpen,
  onClose,
}: QuoteAnalyticsV2Props) => {
  const { data: recRevSettings } = useRevRecSettings();
  const { canDo } = useACL();
  const { enableRevRecV2 } = useFlags();
  const canUpdateSales = canDo([['sales', 'update']]);
  const [analyticsCards, setAnalyticsCards] = useState<AnalyticsCards | null>(
    null,
  );
  const [internalTabIndex, setInternalTabIndex] = useState(tabIndex);

  const { data: annualBreakdownData, isLoading: isLoadingAnnualBreakdown } =
    useQuoteRevenueMetrics(quote.id, {
      enabled: isOpen && internalTabIndex === 2,
    });

  useEffect(() => {
    if (quote && isOpen) {
      setAnalyticsCards({
        arr: getAnalyticsItem(quote, 'ARR', 'arr', 'incrementalArr'),
        mrr: getAnalyticsItem(quote, 'MRR', 'mrr', 'incrementalMrr'),
      });
    }
  }, [quote, isOpen]);
  const hasManualRenewalFormValue =
    manualRenewalFormMethods?.getValues('previousArr');

  const canShowRevenueTab =
    quote.status === QuoteStatusEnum.PROCESSED &&
    canUpdateSales &&
    enableRevRecV2 &&
    recRevSettings?.enabled;

  return (
    <MBox position="relative" display="flex" alignItems="center">
      {renderTrigger ? (
        renderTrigger()
      ) : (
        <MDropdownActionItem
          key={'Quote Analytics'}
          onClick={onOpen}
          color="tPurple.base"
          display="flex"
          aria-label="Quote Analytics"
          alignItems="center"
          data-testid={`data-table-actions-Analytics`}
          role="group"
          w="100%"
        >
          <MText color="inherit">Quote Analytics</MText>
        </MDropdownActionItem>
      )}
      <MDrawer
        open={isOpen}
        placement="end"
        size="md"
        onOpenChange={(e) => {
          if (!e.open) {
            manualRenewalFormMethods?.reset?.();
            onClose();
          }
        }}
        preventScroll={false}
      >
        <Portal>
          <MDrawerOverlay />
          <MDrawerPositioner>
            <MDrawerContent>
              <MDrawerCloseButton>
                <Icon asChild>
                  <MdClose />
                </Icon>
              </MDrawerCloseButton>
              <MDrawerHeader>Quote Analytics</MDrawerHeader>

              <MDrawerBody p={0} bg="tGray.drawerSidebar">
                {analyticsCards && (
                  <MTabs
                    variant="line"
                    size="sm"
                    backgroundColor="tWhite.base"
                    index={internalTabIndex}
                    onChange={(index) => setInternalTabIndex(index)}
                  >
                    <MTabList px={6} pb={2}>
                      <MTab>Annual</MTab>
                      <MTab>Monthly</MTab>
                      <MTab>Annual Breakdown</MTab>
                      {canShowRevenueTab && <MTab>Revenue</MTab>}
                    </MTabList>
                    <MTabPanels p={2} bg="tGray.sidebarDark">
                      <MTabPanel p={0}>
                        <QuoteAnalyticsInfoCard
                          text={[
                            'Annual Recurring Revenue (ARR) represents the annualized revenue from recurring products. Only offerings that end on the same date as the contract are included in the Quote level ARR.',
                            'Incremental ARR represents the change in annual recurring revenue for this quote compared to the prior quote. Only products that have been added, removed, or modified will have an incremental value.',
                          ]}
                        />
                        <QuoteAnalyticsCard
                          currency={quote.currency}
                          label={analyticsCards.arr.label}
                          amount={analyticsCards.arr.amount}
                          incrementalAmount={
                            analyticsCards.arr.incrementalAmount
                          }
                          offerings={analyticsCards.arr.offerings}
                        />
                        {/* MANUAL RENEWAL FORM */}
                        {!!manualRenewalFormMethods &&
                          !!hasManualRenewalFormValue &&
                          quote.status === QuoteStatusEnum.DRAFT && (
                            <QuoteAnalyticsManualRenewalForm
                              isReadOnly={isReadOnly}
                              quote={quote}
                              setQuote={setQuote}
                              manualRenewalForm={manualRenewalFormMethods}
                            />
                          )}
                      </MTabPanel>
                      <MTabPanel p={0}>
                        <QuoteAnalyticsInfoCard
                          text={[
                            'Monthly Recurring Revenue (MRR) represents the monthly portion of recurring revenue. Only offerings that end on the same date as the contract are included in the Quote level MRR.',
                            'Incremental MRR represents the change in monthly recurring revenue for this quote compared to the prior quote. Only products that have been added, removed, or modified will have an incremental value.',
                          ]}
                        />
                        <QuoteAnalyticsCard
                          currency={quote.currency}
                          label={analyticsCards.mrr.label}
                          amount={analyticsCards.mrr.amount}
                          incrementalAmount={
                            analyticsCards.mrr.incrementalAmount
                          }
                          offerings={analyticsCards.mrr.offerings}
                        />
                      </MTabPanel>
                      <MTabPanel p={0}>
                        <QuoteAnalyticsInfoCard
                          text={[
                            'ARR and MRR for each year are calculated based on the recurring offerings that are active on the final day of that year’s contract period.',
                          ]}
                        />
                        {isLoadingAnnualBreakdown && (
                          <MSkeleton height={'200px'} />
                        )}
                        {!isLoadingAnnualBreakdown &&
                          annualBreakdownData?.revenueByYear &&
                          annualBreakdownData.revenueByYear.map(
                            (data, index) => (
                              <QuoteAnnualBreakdownAnalyticsCard
                                currency={quote.currency}
                                data={data}
                                key={data.end}
                                yearLabel={index + 1}
                              />
                            ),
                          )}
                      </MTabPanel>
                      <MTabPanel p={0}>
                        <QuoteAnalyticsRevenueCard quote={quote} />
                      </MTabPanel>
                    </MTabPanels>
                  </MTabs>
                )}
              </MDrawerBody>
              <MDrawerFooter borderTop="1px" borderColor="inherit">
                <MBox w="full">
                  <MFlex justifyContent="center">
                    <MButton
                      variant="cancel"
                      justifySelf="center"
                      onClick={onClose}
                    >
                      Close
                    </MButton>
                  </MFlex>
                </MBox>
              </MDrawerFooter>
            </MDrawerContent>
          </MDrawerPositioner>
        </Portal>
      </MDrawer>
    </MBox>
  );
};
